import localPosts from "@/content/blog-posts.json";

// Shape of a post as extracted from the CodeIgniter `blog` table by
// scripts/extract-blog-from-sql.mjs, and as lib/cms.ts maps CMS entries onto.

export type BlogPost = {
  id: number;
  slug: string;
  /** Display title on cards and the detail hero. */
  heading: string;
  /** <title> for the post; CI stored these separately from `heading`. */
  title: string;
  /** Meta description, also used as the card excerpt. */
  description: string;
  keywords: string;
  author: string;
  category: string;
  categoryId: number;
  /** Local path under /public, e.g. /blog-images/abc.jpg */
  image: string;
  imageOriginal: string;
  /** Post body — raw HTML authored in the CI admin's rich-text editor. */
  body: string;
  /** ISO yyyy-mm-dd, normalised from published_at / created_date. */
  date: string;
  createdDate: string;
  /** 0 => Active, 1 => Inactive, per the column comment in the dump. */
  status: number;
  /**
   * Where this post came from. Legacy posts were migrated out of the old
   * MySQL blog and stay in whatever language they were written in. Posts
   * authored in the CMS get a translated /hi route.
   */
  source: "legacy" | "cms";
};

/** Posts extracted from the MySQL dump — the fallback when the CMS is empty. */
export const localArticles = localPosts as BlogPost[];

export function isPublished(post: BlogPost): boolean {
  return post.status === 0;
}

export function sortByDateDesc(posts: BlogPost[]): BlogPost[] {
  return [...posts].sort((a, b) => (b.date || "").localeCompare(a.date || ""));
}

/** "2023-01-04" -> "Jan, 04 2023", matching the CI views' date() format. */
export function formatCardDate(iso: string): string {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return "";
  const month = d.toLocaleString("en-US", { month: "short", timeZone: "UTC" });
  return `${month}, ${String(d.getUTCDate()).padStart(2, "0")} ${d.getUTCFullYear()}`;
}

/** "2023-01-04" -> "Jan 04, 2023", the detail page's format. */
export function formatDetailDate(iso: string): string {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return "";
  const month = d.toLocaleString("en-US", { month: "short", timeZone: "UTC" });
  return `${month} ${String(d.getUTCDate()).padStart(2, "0")}, ${d.getUTCFullYear()}`;
}

export function slugifyCategory(category: string): string {
  return category
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** Card/sidebar data keeps the post shape without shipping unused article HTML. */
export function withoutBody(post: BlogPost): BlogPost {
  return {
    ...post,
    body: "",
    keywords: "",
    title: "",
    imageOriginal: "",
    createdDate: "",
  };
}
